import { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Table } from 'antd'
import { STUDENT } from '@BUP/constants'
import AlertComponent from '@BUP/components/Generics/Alert'
import PageLayout from '@BUP/components/Layout/Page'
import CourseSelect from '@BUP/components/Generics/Selects/Courses'
import { CurrentSociogramContext } from '@BUP/contexts/CurrentSociogramContext'
import { useGetUsersListByCourse } from '@BUP/hooks/Enrollments/useGetUsersListByCourse'
import { useGetCourseRegistrations } from '@BUP/hooks/registration/useGetCourseRegistrations'
import styles from './students-by-course.module.scss'
import { getStudentAndParentsTableColumns } from '@BUP/components/MySchool/StudentTableList/table-columns.jsx'
import { useIsFinanceReadOnly } from '@BUP/hooks/useIsFinanceReadOnly'
import AddStudentButton from './add-student-button.jsx'

const StudentsByCourse = () => {
  const i18n = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { gradeName, filterCoursesByGrade, courses } = location.state || {}
  const [t] = useTranslation()
  const isReadOnly = useIsFinanceReadOnly()
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const { dataCurrentSociogram } = useContext(CurrentSociogramContext)
  const { currentSociogram } = dataCurrentSociogram || {}

  let isActiveSurvey = false
  if (currentSociogram && selectedCourse) {
    const findSurvey = currentSociogram.surveys?.find(survey => {
      return survey.courses.some(course => course.id === selectedCourse)
    })
    isActiveSurvey = findSurvey && findSurvey.isActive
  }

  const {
    usersList: students,
    isLoadingUsers,
    usersTotalCount,
    getUsersList
  } = useGetUsersListByCourse(selectedCourse, STUDENT, {
    page: currentPage,
    limit: pageSize
  })

  const {
    isLoadingCourseRegistrations,
    courseRegistrations,
    getRegistrations
  } = useGetCourseRegistrations(selectedCourse)

  if (!filterCoursesByGrade) {
    navigate('/schoolpage/studentsgrades')
  }

  const handleSelectCourse = courseId => {
    setSelectedCourse(courseId)
    setCurrentPage(1)
  }

  const handleStudentsPaginationChange = (page, nextPageSize) => {
    setPageSize(nextPageSize)
    setCurrentPage(nextPageSize !== pageSize ? 1 : page)
  }

  const registrationsList = Array.isArray(courseRegistrations)
    ? courseRegistrations
    : []

  const dataStudentList = students.map(student => {
    return {
      key: student.id,
      enrollmentId: student.id,
      name: student.user.name,
      email: student.user.email,
      userId: student.userId,
      courseId: selectedCourse
    }
  })

  const totalStudents = usersTotalCount ?? dataStudentList.length

  const columns = getStudentAndParentsTableColumns({
    i18n,
    selectedCourse,
    courseRegistrations: registrationsList,
    getRegistrations,
    isActiveSurvey,
    courses,
    getUsersList,
    canWrite: !isReadOnly
  })

  return (
    <PageLayout
      pageName={gradeName}
      backButton
    >
      <div className={styles.container}>
        <div className={styles.select_wrapper}>
          <CourseSelect
            onChange={e => handleSelectCourse(e)}
            placeholder={t('pages.MySchool.placeholderSelect')}
            filterCourses={filterCoursesByGrade}
          />
        </div>
        {selectedCourse
          ? (
            <>
              {!isReadOnly && (
                <AddStudentButton
                  selectedCourse={selectedCourse}
                  getUsersList={getUsersList}
                  getRegistrations={getRegistrations}
                  isActiveSurvey={isActiveSurvey}
                />
              )}
              {totalStudents > 0
                ? (
                  <h2 className={styles.total_text}>Total: {totalStudents}</h2>
                  )
                : null}
              <Table
                columns={columns}
                loading={isLoadingUsers || isLoadingCourseRegistrations}
                dataSource={dataStudentList}
                bordered={false}
                pagination={{
                  position: ['none', 'bottomCenter'],
                  current: currentPage,
                  pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['5', '10', totalStudents],
                  total: totalStudents,
                  onChange: handleStudentsPaginationChange
                }}
                className={styles.table}
              />
            </>
            )
          : (
            <div
              className={styles.alert_wrapper}
            >
              <AlertComponent
                type='info'
                message={t('pages.MySchool.alertText.noSelectionText')}
              />
            </div>
            )}
      </div>
    </PageLayout>
  )
}

export default StudentsByCourse
